import { EntityConfig } from './types';
import { Badge } from '@/components/ui/badge';
import { User } from 'lucide-react';

export interface Candidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_primary: string | null;
  phone_primary: string | null;
  city: string | null;
  address: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  website_url: string | null;
  employment_status: string | null;
  availability_status: string | null;
  availability_date: string | null;
  desired_salary_min: number | null;
  desired_salary_max: number | null;
  desired_salary_currency: string | null;
  preferred_contact_method: string | null;
  ai_skills_summary: string | null;
  ai_products_summary: string | null;
  created_at: string;
  updated_at: string | null;
}

const EMPLOYMENT_STATUS_OPTIONS = [
  { label: 'Employed', value: 'employed' },
  { label: 'Unemployed', value: 'unemployed' },
  { label: 'Student', value: 'student' },
  { label: 'Freelancer', value: 'freelancer' },
];

const AVAILABILITY_STATUS_OPTIONS = [
  { label: 'Available', value: 'available' },
  { label: 'Not Available', value: 'not_available' },
  { label: 'Open to Offers', value: 'open_to_offers' },
  { label: 'Notice Period', value: 'notice_period' },
];

const CONTACT_METHOD_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'LinkedIn', value: 'linkedin' },
];

export const candidatesConfig: EntityConfig<Candidate> = {
  name: 'candidates',
  singularName: 'Candidate',
  pluralName: 'Candidates',
  tableName: 'candidates',
  keyField: 'id',
  icon: <User className="h-5 w-5" />,
  description: 'Manage candidate profiles and information',
  
  permissionModule: 'candidates',
  
  defaultSelect: '*',
  defaultOrderBy: { field: 'created_at', ascending: false },
  searchFields: ['first_name', 'last_name', 'email_primary', 'phone_primary'],
  defaultPageSize: 25,
  
  columns: [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
      sortable: true,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email_primary',
      cell: (row) => row.email_primary || '-',
      sortable: true,
    },
    {
      id: 'phone',
      header: 'Phone',
      accessorKey: 'phone_primary',
      cell: (row) => row.phone_primary || '-',
      sortable: false,
      defaultVisible: false,
    },
    {
      id: 'city',
      header: 'City',
      accessorKey: 'city',
      cell: (row) => row.city || '-',
      sortable: true,
    },
    {
      id: 'availability',
      header: 'Availability',
      accessorKey: 'availability_status',
      cell: (row) => (
        <Badge 
          variant={row.availability_status === 'available' ? 'default' : 'secondary'}
          className="capitalize"
        >
          {row.availability_status?.replace('_', ' ') || 'Unknown'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'employment',
      header: 'Status',
      accessorKey: 'employment_status',
      cell: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.employment_status || 'Unknown'}
        </Badge>
      ),
      sortable: true,
      defaultVisible: false,
    },
  ],
  
  formFields: [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      required: true,
      placeholder: 'John',
      gridSpan: 1,
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      required: true,
      placeholder: 'Doe',
      gridSpan: 1,
    },
    {
      name: 'email_primary',
      label: 'Email',
      type: 'email',
      placeholder: 'john.doe@example.com',
      gridSpan: 1,
    },
    {
      name: 'phone_primary',
      label: 'Phone',
      type: 'phone',
      placeholder: '+1 234 567 8900',
      gridSpan: 1,
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'New York',
      gridSpan: 1,
    },
    {
      name: 'preferred_contact_method',
      label: 'Preferred Contact',
      type: 'select',
      options: CONTACT_METHOD_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'employment_status',
      label: 'Employment Status',
      type: 'select',
      options: EMPLOYMENT_STATUS_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'availability_status',
      label: 'Availability',
      type: 'select',
      options: AVAILABILITY_STATUS_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'availability_date',
      label: 'Available From',
      type: 'date',
      showIf: (values) => values.availability_status === 'notice_period',
      gridSpan: 1,
    },
    {
      name: 'desired_salary_currency',
      label: 'Currency',
      type: 'select',
      options: [
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' },
        { label: 'GBP', value: 'GBP' },
        { label: 'INR', value: 'INR' },
      ],
      gridSpan: 1,
    },
    {
      name: 'desired_salary_min',
      label: 'Expected Salary (Min)',
      type: 'number',
      placeholder: '50000',
      gridSpan: 1,
    },
    {
      name: 'desired_salary_max',
      label: 'Expected Salary (Max)',
      type: 'number',
      placeholder: '80000',
      gridSpan: 1,
    },
    {
      name: 'linkedin_url',
      label: 'LinkedIn URL',
      type: 'url',
      placeholder: 'https://linkedin.com/in/...',
      gridSpan: 1,
    },
    {
      name: 'github_url',
      label: 'GitHub URL',
      type: 'url',
      placeholder: 'https://github.com/...',
      gridSpan: 1,
    },
    {
      name: 'website_url',
      label: 'Website',
      type: 'url',
      placeholder: 'https://...',
      gridSpan: 2,
    },
    {
      name: 'address',
      label: 'Address',
      type: 'textarea',
      placeholder: 'Full address...',
      gridSpan: 2,
    },
  ],
  
  filters: [
    {
      id: 'availability',
      label: 'Availability',
      field: 'availability_status',
      type: 'select',
      options: [
        { label: 'All', value: 'all' },
        ...AVAILABILITY_STATUS_OPTIONS,
      ],
      defaultValue: 'all',
    },
    {
      id: 'employment',
      label: 'Employment',
      field: 'employment_status',
      type: 'select',
      options: [
        { label: 'All', value: 'all' },
        ...EMPLOYMENT_STATUS_OPTIONS,
      ],
      defaultValue: 'all',
    },
  ],
  
  audit: {
    enabled: true,
    excludeFields: ['updated_at', 'ai_skills_summary', 'ai_products_summary'],
    detailedChangelog: true,
  },
  
  notifications: {
    onCreate: {
      eventType: 'candidate_created',
      recipients: 'admins',
    },
  },
  
  routes: {
    list: '/recruiter/candidates',
    detail: '/recruiter/candidates/:id',
  },
  
  features: {
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canBulkDelete: true,
    canExport: true,
    canSearch: true,
    showActivityLog: true,
  },
};
